'use client';

// 배럴(Assets/Icon)이 아니라 파일을 직접 import 한다 — RenewalNoticeModal 과 같은 이유.
import SvgRenewalClose from 'Assets/Icon/svgs/RenewalClose.svg';
import PhoneImage from 'Assets/Images/AutoViewingEndPhone.png';
import { Button, Column, Modal, Typography } from '@viewup/ui';
import Image from 'next/image';

export interface AutoViewingEndNoticeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onClickNotice: () => void;
  /** '오늘 하루 안보기' 버튼. onClose 와 달리 다음 로컬 자정까지 다시 열지 않게 하는 쪽이다. */
  onHideToday: () => void;
}

// 피그마 디자인(오토뷰업 서비스 종료 팝업)의 색. 대응하는 theme 토큰이 없다.
const DESIGN_COLOR = {
  brand: '#0077FF',
  textPrimary: '#001E40',
  textSecondary: '#576A7C',
};

// 카드 360×482. 위에서부터: padding-top 40 → 텍스트 블록 168(제목 44×2 + gap 8 + 본문 24×3) = 208
// → 일러스트까지 18 → 일러스트 110(= 226~336) → 버튼까지 20 → 버튼 52(= 356~408) → 20
// → 오늘 하루 안보기 22(= 428~450) → padding-bottom 32.
const ROOT_CSS = {
  background: 'linear-gradient(180deg, #FFFFFF 0%, #FBFBFE 35%, #D3DCFF 100%)',
  borderRadius: 8,
  maxWidth: 360,
  overflow: 'hidden',
  whiteSpace: 'normal',
} as const;

// sds Button(size XL)은 고정 height·좌우 padding·body.regular 라벨이라 디자인(높이 52, 라벨 Bold 18/28,
// 폭 100%)과 맞지 않아 보충한다. 라벨은 Button 내부 Typography(span)를 겨냥한다.
// hover/active 는 variant 가 :hover/:active 로 색을 덮어쓰므로 같은 셀렉터로 다시 눌러준다.
const NOTICE_BUTTON_CSS = {
  '& span': { fontSize: 18, fontWeight: 700, lineHeight: '28px' },
  ':active': { backgroundColor: DESIGN_COLOR.brand },
  ':hover': { backgroundColor: DESIGN_COLOR.brand },
  backgroundColor: DESIGN_COLOR.brand,
  borderRadius: 8,
  boxSizing: 'border-box',
  color: '#FFFFFF',
  height: 52,
  minHeight: 0,
  paddingBlock: 12,
  width: '100%',
} as const;

const HIDE_TODAY_BUTTON_STYLE = {
  background: 'none',
  border: 0,
  color: DESIGN_COLOR.textSecondary,
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 14,
  fontWeight: 400,
  lineHeight: '22px',
  // 터치 영역을 키운다(위아래 +8). 시각 간격은 marginTop 12 + padding 8 = 20 으로 디자인 그대로.
  marginTop: 12,
  padding: '8px 12px',
  textAlign: 'center',
  textDecoration: 'underline',
} as const;

const CLOSE_BUTTON_STYLE = {
  background: 'none',
  border: 0,
  cursor: 'pointer',
  display: 'block',
  height: 32,
  padding: 0,
  position: 'absolute',
  right: 20,
  top: 20,
  width: 32,
} as const;

export const AutoViewingEndNoticeModal = ({
  isOpen,
  onClose,
  onClickNotice,
  onHideToday,
}: AutoViewingEndNoticeModalProps) => {
  return (
    <Modal
      exitHandler={onClose}
      hasCloseButton={false}
      height='auto'
      isOpen={isOpen}
      isResponsive={false}
      styledComponents={{ root: { css: ROOT_CSS, p: 0 } }}
      width='calc(100% - 40px)'
    >
      <button aria-label='닫기' onClick={onClose} style={CLOSE_BUTTON_STYLE} type='button'>
        <Image alt='' height={32} src={SvgRenewalClose} width={32} />
      </button>

      <Column alignItems='center' css={{ boxSizing: 'border-box', padding: '40px 20px 24px' }}>
        <Column alignItems='center' gap={8}>
          <Typography
            css={{
              color: DESIGN_COLOR.textPrimary,
              fontSize: 32,
              fontWeight: 700,
              lineHeight: '44px',
              textAlign: 'center',
            }}
            fontType='header2.bold'
          >
            <span style={{ display: 'block' }}>오토뷰업</span>
            <span style={{ display: 'block' }}>PC 서비스 종료</span>
          </Typography>
          <Typography
            css={{ color: DESIGN_COLOR.textSecondary, lineHeight: '24px', textAlign: 'center' }}
            fontType='body.regular'
          >
            <span style={{ display: 'block' }}>PC 및 모바일 웹에서의</span>
            <span style={{ display: 'block' }}>오토뷰업 이용이 종료됩니다.</span>
            <span style={{ display: 'block' }}>오토뷰업은 뷰업 앱에서 이용해 주세요.</span>
          </Typography>
        </Column>

        <Image
          alt=''
          height={110}
          src={PhoneImage}
          style={{ marginBottom: 20, marginTop: 18 }}
          width={93}
        />

        <Button css={NOTICE_BUTTON_CSS} onClick={onClickNotice} size='XL' variant='solid.primary'>
          공지사항 바로가기
        </Button>

        <button onClick={onHideToday} style={HIDE_TODAY_BUTTON_STYLE} type='button'>
          오늘 하루 안보기
        </button>
      </Column>
    </Modal>
  );
};
